import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import { getOrderDetails } from 'yoda-interfaces/lib/Order/OrderApi';
import get from 'lodash/get';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import {
    X_CLIENT_SOURCE_MINI_PDP,
    X_CLIENT_SOURCE_PDP,
    SET_SERVICE_ERROR,
} from 'yoda-site-components/lib/common/Constants';
import ItemCountAction from 'yoda-site-components/lib/actions/ItemCountAction';
import LoadingAction from 'yoda-site-components/lib/actions/LoadingAction';
import { updateCartCount, addToCart } from 'yoda-site-components/lib/actions/NativeAppAction';
import { showCartATCSlider } from 'yoda-site-components/lib/actions/AtcSliderStatusAction';
import { getDisableBYDeliveryDateFlag } from 'yoda-site-components/lib/selectors/byDeliveryDateSelector';
import { selectedProductSkus } from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import { updateCartUID } from '../helpers/Utils/ATCCommonUtils';
import { showGlobalLoader } from '../actions/ShowGlobalLoaderAction';
import {
    ANALYTICS_ACTIONS_OPTIONAL_SERVICE_UPDATE_CART,
    ANALYTICS_ACTIONS_OPTIONAL_SERVICE_ADD_CART,
    ANALYTICS_ACTIONS_OPTIONAL_SERVICE_REMOVE_CART,
} from '../common/Constants';
import { ANALYTICS_CLICK_EVENT } from '../actionTypes/CheckoutAnalyticsActionTypes';
import {
    ADDTOCART_DETAILS_GET_SUCCESS,
    ADDTOCART_DETAILS_GET_ERROR,
    ADDTOCART_DETAILS_GET_REQUEST,
} from '../actionTypes/AddToCartInterstitialActionTypes';
import { getCartEventData } from '../helpers/Utils/Utils';

function* CartInterstitalSaga(action) {
    const modifyAction = action;
    try {
        if (!modifyAction.payload) {
            modifyAction.payload = {};
            yield put(LoadingAction.showLoader());
        }
        modifyAction.payload.origin = 'pdp';
        const disableBYDeliveryDate = yield select(getDisableBYDeliveryDateFlag);
        if (!disableBYDeliveryDate) {
            const fromMiniPDP = get(modifyAction, 'payload.fromMiniPDP', false);
            modifyAction.payload.xClientSource = fromMiniPDP
                ? X_CLIENT_SOURCE_MINI_PDP
                : X_CLIENT_SOURCE_PDP;
        }
        const stateVariable = yield select((state) => state);
        const enableCAHaulAwayService = get(
            stateVariable,
            'context.featureFlags.enableCAHaulAwayService',
            true
        );
        if (enableCAHaulAwayService) {
            const fromMiniPDP =
                get(modifyAction, 'payload.fromMiniPDP') ||
                get(modifyAction, 'payload.sourceMiniPDP', false);
            const selectedProductLot = yield select((state) =>
                selectedProductSkus(fromMiniPDP)(state)
            );
            const pathName = get(window, 'location.pathname', '');
            const isCart = pathName.includes('/cart');
            if (selectedProductLot && selectedProductLot.length > 0 && !isCart) {
                const isTruckable = get(selectedProductLot[0], 'shipping.truckable', false);
                if (isTruckable) modifyAction.payload.expand = 'delivery';
            }
        }

        // To get DA Action Events
        const DAEventAction = get(modifyAction, 'DAPayload.DAEventAction', '');
        const optionalServiceUpdateAction =
            DAEventAction === ANALYTICS_ACTIONS_OPTIONAL_SERVICE_UPDATE_CART;
        const optionalServiceAddAction =
            DAEventAction === ANALYTICS_ACTIONS_OPTIONAL_SERVICE_ADD_CART;
        const optionalServiceRemoveAction =
            DAEventAction === ANALYTICS_ACTIONS_OPTIONAL_SERVICE_REMOVE_CART;

        const cartDetails = yield call(FactorySaga, getOrderDetails, modifyAction);
        const isNative = get(stateVariable, 'context.isNative', false);
        yield put(showGlobalLoader(false));
        if (cartDetails.isSuccess) {
            const { response } = cartDetails;
            let itemCount = response.headers.get('X-Item-Count');
            const responseData = response.data;
            const findCartTotal = (totals) => {
                const merchandiseSubTotal = find(totals, { type: 'SUB TOTAL' });
                return merchandiseSubTotal && merchandiseSubTotal.amount
                    ? merchandiseSubTotal.amount.toFixed(2)
                    : '0.00';
            };
            const cartTotal = findCartTotal(responseData.totals);
            LocalStorage.setData('cartTotal', cartTotal);
            // To Add or Update UID Cookie
            !__SERVER__ && updateCartUID(response);
            if (isNil(itemCount)) {
                // If the header is empty then check in the response for bag item count.
                // When 0 is passed it will read from the itemCount cookie
                itemCount = (responseData.bag && responseData.bag.itemCount) || 0;
            }
            if (isNative) {
                const cartItemsData = getCartEventData(responseData.bag.items);
                yield all([
                    put(addToCart(cartItemsData)),
                    put(updateCartCount(itemCount, cartItemsData)),
                ]);
            }
            // TODO: use all effect for below
            yield put({
                type: ADDTOCART_DETAILS_GET_SUCCESS,
                cartDetailsResponse: responseData,
            });
            yield put(ItemCountAction.updateItemCountAction(itemCount));
            yield put(LoadingAction.removeLoader());
            // To trigger DA Action Events
            // eslint-disable-next-line no-unused-expressions
            optionalServiceUpdateAction
                ? yield all([
                      put({
                          type: ANALYTICS_CLICK_EVENT,
                          payload: ANALYTICS_ACTIONS_OPTIONAL_SERVICE_REMOVE_CART,
                      }),
                      put({
                          type: ANALYTICS_CLICK_EVENT,
                          payload: ANALYTICS_ACTIONS_OPTIONAL_SERVICE_UPDATE_CART,
                      }),
                  ])
                : '';
            // eslint-disable-next-line no-unused-expressions
            optionalServiceRemoveAction
                ? yield put({
                      type: ANALYTICS_CLICK_EVENT,
                      payload: ANALYTICS_ACTIONS_OPTIONAL_SERVICE_REMOVE_CART,
                  })
                : '';
            // eslint-disable-next-line no-unused-expressions
            optionalServiceAddAction
                ? yield put({
                      type: ANALYTICS_CLICK_EVENT,
                      payload: ANALYTICS_ACTIONS_OPTIONAL_SERVICE_ADD_CART,
                  })
                : '';
        } else {
            yield all([put(LoadingAction.removeLoader())]);
            /** handle ServiceError Message Box in cartPageWrapper when add/Edit */
            const { errorHandler } = modifyAction.payload;
            errorHandler.errorComponent = 'CART_SECTION';
            errorHandler.key = 'CART_SECTION';
            // eslint-disable-next-line no-unused-expressions
            !DAEventAction
                ? yield all([
                      put({
                          type: SET_SERVICE_ERROR,
                          errorHandlerInfo: errorHandler,
                      }),
                      put(showCartATCSlider(false)),
                  ])
                : '';
        }
    } catch (error) {
        yield all([
            put({ type: ADDTOCART_DETAILS_GET_ERROR, error }),
            put(showGlobalLoader(false)),
            put(LoadingAction.removeLoader()),
            put(showCartATCSlider(false)),
        ]);
    }
}

const watchAddToCartInterstitialSaga = function* watchAddToCartInterstitialSaga() {
    yield takeLatest(ADDTOCART_DETAILS_GET_REQUEST, CartInterstitalSaga);
};

watchAddToCartInterstitialSaga.sagaName = 'watchAddToCartInterstitialSaga';
export default watchAddToCartInterstitialSaga;
